import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Clock, ArrowLeft, ArrowRight, Check, Mail, Wrench, Droplets, Zap as ElectricIcon, Hammer, HelpCircle, Users } from "lucide-react";
import TurnstileWidget from "@/components/TurnstileWidget";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Industry cards shown on Step 2. Codes MUST match /app/backend/industry_presets.py.
// Order on screen = order in this array. HVAC first per the Feb 2026 brief.
const INDUSTRY_CARDS = [
  { code: "hvac",                 label: "HVAC",                                      Icon: Wrench,     hint: "Service, installs, agreements, callbacks" },
  { code: "plumbing_electrical",  label: "Plumbing & Electrical",                     Icon: ElectricIcon, hint: "Service calls, repairs, rough-ins" },
  { code: "exterior_cleaning",    label: "Exterior Cleaning / Pressure Washing",      Icon: Droplets,   hint: "House, roof, flatwork, commercial" },
  { code: "roofing_landscaping",  label: "Roofing & Landscaping",                     Icon: Hammer,     hint: "Replacements, repairs, maintenance" },
  { code: "other",                label: "Other",                                     Icon: HelpCircle, hint: "Generic — we'll keep it simple" },
];

const TEAM_SIZE_OPTIONS = [
  { code: "1_3",   label: "1 – 3",   plan: "starter",  hint: "Starter · $39.99/mo" },
  { code: "4_10",  label: "4 – 10",  plan: "pro",      hint: "Pro · $79.99/mo (most popular)" },
  { code: "11_plus", label: "11+",   plan: "business", hint: "Business · $199/mo" },
];

export default function SignupWizard() {
  const [params] = useSearchParams();
  const planId = params.get("plan") || "";
  const interval = params.get("interval") || "monthly";
  const navigate = useNavigate();

  // Marketing attribution — captured once when this component mounts and
  // forwarded on submit. Persisted in sessionStorage so a click that lands
  // on / (LandingPage) with UTMs, then navigates to /signup, still passes
  // the attribution through.
  const attribution = React.useMemo(() => {
    const fromUrl = {
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      utm_term: params.get("utm_term") || "",
      utm_content: params.get("utm_content") || "",
    };
    let stored = {};
    try { stored = JSON.parse(sessionStorage.getItem("ppt_attribution") || "{}"); } catch {}
    // URL wins over stored (fresh click overrides earlier one)
    const merged = { ...stored, ...Object.fromEntries(Object.entries(fromUrl).filter(([, v]) => v)) };
    if (Object.keys(merged).length) {
      try { sessionStorage.setItem("ppt_attribution", JSON.stringify(merged)); } catch {}
    }
    return {
      ...merged,
      referrer: (typeof document !== "undefined" && document.referrer) || "",
      landing_path: (typeof window !== "undefined" && window.location.pathname) || "",
    };
  }, [params]);

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [resentOnce, setResentOnce] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  // Live plan catalog (fetched once from `/api/billing/plans`). Powers the
  // Meta Pixel `StartTrial` value so the event's `value` always reflects
  // the real monthly price of the plan the user chose. Falls back to a
  // hardcoded price map (and finally the Starter price) only when the
  // fetch hasn't completed or the plan id isn't in the catalog.
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/billing/plans")
      .then(({ data }) => { if (!cancelled) setPlans(data.plans || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const onCaptchaToken = useCallback((token) => setCaptchaToken(token), []);
  // State-pack directory fetched from /api/public/state-packs so the
  // signup wizard can preview daily-OT rules + auto-primed tax rate.
  const [statePacks, setStatePacks] = React.useState([]);
  const [industries, setIndustries] = React.useState({ primary: [], secondary: [] });
  React.useEffect(() => {
    api.get("/public/state-packs")
      .then((r) => setStatePacks(r.data?.states || []))
      .catch(() => setStatePacks([]));
    api.get("/public/industries")
      .then((r) => setIndustries(r.data || { primary: [], secondary: [] }))
      .catch(() => setIndustries({ primary: [], secondary: [] }));
  }, []);

  const [form, setForm] = useState({
    email: "",
    password: "",
    password2: "",
    first_name: "",
    last_name: "",
    company_name: "",
    company_phone: "",
    company_street: "",
    company_city: "",
    company_state: "",
    company_zip: "",
    industry: params.get("industry") || "",      // pre-select from /hvac → ?industry=hvac
    team_size: "",                                 // one of TEAM_SIZE_OPTIONS codes
    terms_accepted: false,
  });

  // Team-size pre-selects the plan the owner is pushed toward on the
  // billing screen. The signup payload passes it as `plan_id` so the
  // Admin > Billing page can highlight it. Nothing is charged — the
  // 14-day free trial still runs with no card on file.
  const effectivePlanId = React.useMemo(() => {
    if (planId) return planId;  // explicit ?plan=xxx from a marketing CTA wins
    const match = TEAM_SIZE_OPTIONS.find((t) => t.code === form.team_size);
    return match?.plan || "";
  }, [planId, form.team_size]);

  const selectedPack = statePacks.find((s) => s.code === form.company_state);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validateStep1 = () => {
    if (!form.email.includes("@")) return "Please enter a valid email";
    if (form.password.length < 8) return "Password must be at least 8 characters";
    if (form.password !== form.password2) return "Passwords don't match";
    return null;
  };
  const validateStep2 = () => {
    if (!form.industry) return "Pick the industry that best describes your business";
    if (!form.team_size) return "Pick how many techs or crew you have";
    return null;
  };
  const validateStep3 = () => {
    if (!form.first_name.trim()) return "First name is required";
    if (!form.last_name.trim()) return "Last name is required";
    if (!form.company_name.trim() || form.company_name.length < 2) return "Company name is required";
    if (!form.company_state) return "Please select the state you're operating in";
    if (!form.terms_accepted) return "Please accept the Terms of Service and Privacy Policy to continue";
    return null;
  };

  const handleNext = () => {
    const err = validateStep1();
    if (err) { toast.error(err); return; }
    setStep(2);
  };

  const handleNextFromIndustry = () => {
    const err = validateStep2();
    if (err) { toast.error(err); return; }
    // Facebook Pixel — IndustrySelected fires the moment the owner
    // commits an industry on Step 2 so we attribute the click even if
    // they drop off before finishing Step 3. Guarded for ad-blockers.
    try {
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("trackCustom", "IndustrySelected", {
          industry: form.industry,
          team_size: form.team_size,
          plan_hint: effectivePlanId || null,
        });
      }
    } catch {}
    setStep(3);
  };

  const handleSubmit = async () => {
    const err = validateStep3();
    if (err) { toast.error(err); return; }
    setBusy(true);
    // Facebook Pixel — Lead fires BEFORE the signup call so we capture the
    // conversion the moment the user commits their email, even if the
    // account-create API fails. Guarded with `if (window.fbq)` so an
    // ad-blocker / script failure doesn't break signup.
    try {
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: planId || "no_plan_selected" });
      }
    } catch {}
    try {
      const { data: signupResp } = await api.post("/auth/signup", {
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
        company_name: form.company_name,
        company_phone: form.company_phone || undefined,
        company_street: form.company_street || undefined,
        company_city: form.company_city || undefined,
        company_state: form.company_state || undefined,
        company_zip: form.company_zip || undefined,
        industry: form.industry || undefined,
        plan_id: effectivePlanId || undefined,
        interval: effectivePlanId ? interval : undefined,
        terms_accepted: true,
        terms_version: "2026-01-01",
        utm_source: attribution.utm_source || undefined,
        utm_medium: attribution.utm_medium || undefined,
        utm_campaign: attribution.utm_campaign || undefined,
        utm_term: attribution.utm_term || undefined,
        utm_content: attribution.utm_content || undefined,
        referrer: attribution.referrer || undefined,
        landing_path: attribution.landing_path || undefined,
        turnstile_token: captchaToken || undefined,
      });
      // Facebook Pixel — StartTrial fires once the trial account was
      // actually created server-side. `value` is the actual monthly
      // price of the plan the user selected, resolved in this order:
      //   1. Live `/api/billing/plans` → selected plan's monthly_price_cents
      //      (so pricing changes auto-track with no code edit).
      //   2. Hardcoded safety map (fetch failed / in-flight at submit).
      //   3. Starter price as the final fallback.
      // Guarded so an ad-blocker / script failure doesn't break the
      // signup success path.
      const START_TRIAL_PLAN_VALUES = {
        starter: 39.99,
        pro: 79.99,
        business: 199.00,
      };
      const livePlan = plans.find((p) => p.id === planId);
      const liveMonthly = livePlan && livePlan.monthly_price_cents != null
        ? livePlan.monthly_price_cents / 100
        : null;
      const startTrialValue = liveMonthly
        ?? START_TRIAL_PLAN_VALUES[planId]
        ?? 39.99;
      try {
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
          window.fbq("track", "StartTrial", {
            value: startTrialValue,
            currency: "USD",
            predicted_ltv: 240,
          });
        }
      } catch {}
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      // Turnstile tokens are single-use — reset after any server rejection.
      setCaptchaToken(null);
      captchaRef.current?.reset();
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email: form.email });
      toast.success("Verification email re-sent");
      setResentOnce(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="rounded-2xl border-slate-200 w-full max-w-md" data-testid="signup-success-card">
          <CardContent className="p-8 text-center space-y-4">
            <div className="h-14 w-14 mx-auto rounded-full bg-green-100 flex items-center justify-center">
              <Mail className="h-7 w-7 text-green-700" />
            </div>
            <h2 className="text-2xl font-extrabold font-display tracking-tighter text-slate-900">Check your inbox</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              We sent a verification link to <strong>{form.email}</strong>. Click it to activate your account.
            </p>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="signup-spam-hint">
              <strong>Don&apos;t see it?</strong> New sender domains sometimes land in <b>spam</b> or Gmail&apos;s <b>Promotions</b> tab for the first email. Check there — then mark it &ldquo;Not spam&rdquo; so future emails go straight to your inbox.
            </div>
            <div className="pt-2 space-y-2">
              <Button onClick={resend} disabled={resentOnce} variant="outline" className="w-full" data-testid="signup-resend-btn">
                {resentOnce ? "Email re-sent" : "Re-send verification email"}
              </Button>
              <Button asChild variant="ghost" className="w-full">
                <Link to="/login">Already verified? Sign in</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const planLabel = planId ? `${planId[0].toUpperCase()}${planId.slice(1)} · ${interval}` : "";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="signup-logo-home">
            <div className="h-8 w-8 rounded-lg bg-sky-600 flex items-center justify-center">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <span className="font-extrabold font-display tracking-tight">Production Pay Tracker</span>
          </Link>
          <Link to="/login" className="text-sm font-semibold text-slate-700 hover:text-slate-900">
            Sign in instead
          </Link>
        </div>
      </header>

      <div className="max-w-md mx-auto px-6 py-12">
        <div className="text-center mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700">Step {step} of 3</p>
          <h1 className="mt-2 text-3xl font-extrabold font-display tracking-tighter text-slate-900">
            {step === 1 ? "Create your account" : step === 2 ? "What kind of company do you run?" : "Tell us about your company"}
          </h1>
          {planId && (
            <p className="mt-2 text-sm text-slate-600">
              You picked the <span className="font-semibold text-slate-900">{planLabel}</span> plan. Account first &mdash; you can review &amp; pay after verifying your email.
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 mb-6">
          <div className={`h-1.5 flex-1 rounded-full ${step >= 1 ? "bg-sky-600" : "bg-slate-200"}`} />
          <div className={`h-1.5 flex-1 rounded-full ${step >= 2 ? "bg-sky-600" : "bg-slate-200"}`} />
          <div className={`h-1.5 flex-1 rounded-full ${step >= 3 ? "bg-sky-600" : "bg-slate-200"}`} />
        </div>

        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-6 space-y-4">
            {step === 1 && (
              <>
                <div>
                  <Label>Work email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@yourcompany.com"
                    autoComplete="email"
                    data-testid="signup-email"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={update("password")}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    data-testid="signup-password"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Confirm password</Label>
                  <Input
                    type="password"
                    value={form.password2}
                    onChange={update("password2")}
                    placeholder="Re-enter password"
                    autoComplete="new-password"
                    data-testid="signup-password2"
                    className="mt-1"
                  />
                </div>
                <Button onClick={handleNext} className="w-full bg-sky-600 hover:bg-sky-700 h-11 mt-2" data-testid="signup-next-btn">
                  Continue <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <p className="text-sm text-slate-600 -mt-1 mb-1">
                  We'll pre-load the right roles, job types, and pay rules for your industry. You can edit everything later.
                </p>
                <div className="grid grid-cols-1 gap-2" data-testid="signup-industry-cards">
                  {INDUSTRY_CARDS.map((card) => {
                    const Icon = card.Icon;
                    const selected = form.industry === card.code;
                    return (
                      <button
                        key={card.code}
                        type="button"
                        onClick={() => setForm({ ...form, industry: card.code })}
                        data-testid={`industry-card-${card.code}`}
                        className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition ${
                          selected
                            ? "border-sky-600 bg-sky-50 ring-2 ring-sky-200"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${selected ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-700"}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className={`font-bold text-sm ${selected ? "text-sky-900" : "text-slate-900"}`}>{card.label}</p>
                          <p className="text-xs text-slate-500 truncate">{card.hint}</p>
                        </div>
                        {selected && <Check className="h-4 w-4 text-sky-600 ml-auto shrink-0" />}
                      </button>
                    );
                  })}
                </div>

                <div className="pt-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-slate-600" />
                    <Label className="m-0">How many techs or crew members do you have?</Label>
                  </div>
                  <div className="grid grid-cols-3 gap-2" data-testid="signup-team-size">
                    {TEAM_SIZE_OPTIONS.map((opt) => {
                      const selected = form.team_size === opt.code;
                      return (
                        <button
                          key={opt.code}
                          type="button"
                          onClick={() => setForm({ ...form, team_size: opt.code })}
                          data-testid={`team-size-${opt.code}`}
                          className={`p-3 rounded-xl border-2 text-center transition ${
                            selected
                              ? "border-sky-600 bg-sky-50 ring-2 ring-sky-200"
                              : "border-slate-200 bg-white hover:border-slate-300"
                          }`}
                        >
                          <p className={`font-extrabold text-lg ${selected ? "text-sky-900" : "text-slate-900"}`}>{opt.label}</p>
                          <p className="text-[10px] text-slate-500 leading-tight mt-0.5">{opt.hint}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="signup-back-btn-2">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                  </Button>
                  <Button
                    onClick={handleNextFromIndustry}
                    disabled={!form.industry || !form.team_size}
                    className="flex-1 bg-sky-600 hover:bg-sky-700 disabled:opacity-60"
                    data-testid="signup-industry-continue-btn"
                  >
                    Continue <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900 flex items-start gap-2" data-testid="signup-trial-banner">
                  <Clock className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-bold">14-day free trial starts right after this.</p>
                    <p className="mt-0.5 text-emerald-800">
                      <b>No credit card required.</b> Add techs, log jobs, run your first paystub. Pick a plan whenever you're ready — your account becomes read-only on day 15 if you don't upgrade, and your data stays safe.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>First name</Label>
                    <Input value={form.first_name} onChange={update("first_name")} data-testid="signup-first-name" className="mt-1" />
                  </div>
                  <div>
                    <Label>Last name</Label>
                    <Input value={form.last_name} onChange={update("last_name")} data-testid="signup-last-name" className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label>Company name</Label>
                  <Input
                    value={form.company_name}
                    onChange={update("company_name")}
                    placeholder="Your Company LLC"
                    data-testid="signup-company-name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Company phone <span className="text-slate-400 text-xs">(optional)</span></Label>
                  <Input value={form.company_phone} onChange={update("company_phone")} placeholder="(555) 123-4567" data-testid="signup-company-phone" className="mt-1" />
                </div>
                <div>
                  <Label>Street <span className="text-slate-400 text-xs">(optional)</span></Label>
                  <Input value={form.company_street} onChange={update("company_street")} data-testid="signup-street" className="mt-1" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Label>City</Label>
                    <Input value={form.company_city} onChange={update("company_city")} data-testid="signup-city" className="mt-1" />
                  </div>
                  <div>
                    <Label>State <span className="text-red-500">*</span></Label>
                    <Select
                      value={form.company_state}
                      onValueChange={(v) => setForm({ ...form, company_state: v })}
                    >
                      <SelectTrigger data-testid="signup-state" className="mt-1">
                        <SelectValue placeholder="Pick state" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {statePacks.map((s) => (
                          <SelectItem key={s.code} value={s.code}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {selectedPack && (
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900" data-testid="signup-state-preview">
                    We'll auto-configure your payroll for <b>{selectedPack.name}</b>:
                    federal withholding 10%, state {selectedPack.state_tax_pct}%,
                    FICA (SS 6.2% + Medicare 1.45%), state min wage ${selectedPack.min_wage.toFixed(2)}
                    {selectedPack.daily_ot_hours && <> · daily OT over {selectedPack.daily_ot_hours} hrs/day</>}.
                    All rates are editable in Payroll Settings after signup.
                  </div>
                )}
                <div>
                  <Label>ZIP</Label>
                  <Input value={form.company_zip} onChange={update("company_zip")} placeholder="12345" data-testid="signup-zip" className="mt-1" />
                </div>
                <label className="flex items-start gap-2.5 pt-3 cursor-pointer group" data-testid="signup-terms-checkbox-wrap">
                  <input
                    type="checkbox"
                    checked={form.terms_accepted}
                    onChange={(e) => setForm({ ...form, terms_accepted: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500 shrink-0"
                    data-testid="signup-terms-checkbox"
                  />
                  <span className="text-xs text-slate-600 leading-relaxed">
                    I agree to the{" "}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-sky-700 font-semibold underline decoration-2 underline-offset-2 hover:text-sky-900" data-testid="signup-terms-link">Terms of Service</Link>
                    {" "}and{" "}
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-sky-700 font-semibold underline decoration-2 underline-offset-2 hover:text-sky-900" data-testid="signup-privacy-link">Privacy Policy</Link>. I understand my subscription auto-renews and can be cancelled anytime from the billing portal.
                  </span>
                </label>
                <TurnstileWidget ref={captchaRef} action="signup" onToken={onCaptchaToken} className="pt-1" />
                <div className="flex items-center gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1" data-testid="signup-back-btn">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                  </Button>
                  <Button onClick={handleSubmit} disabled={busy || !form.terms_accepted} className="flex-1 bg-sky-600 hover:bg-sky-700 disabled:opacity-60" data-testid="signup-submit-btn">
                    {busy ? "Creating…" : "Create account"} {!busy && <Check className="h-4 w-4 ml-1" />}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-slate-500 text-center mt-4">
          Read our <Link to="/terms" className="underline decoration-2 underline-offset-2" data-testid="signup-footer-terms">Terms</Link> and <Link to="/privacy" className="underline decoration-2 underline-offset-2" data-testid="signup-footer-privacy">Privacy Policy</Link>. Your <b>14-day free trial starts right away — no credit card required</b>. Pick a plan whenever you're ready.
        </p>
      </div>
    </div>
  );
}
